// OfflineMessageQueue.js - 오프라인 메시지 큐잉 시스템
class OfflineMessageQueue {
  constructor() {
    this.queue = [];
    this.isOnline = navigator.onLine;
    this.maxRetries = 3;
    this.retryDelay = 5000; // 5초
    this.maxQueueSize = 100;
    
    // 네트워크 상태 감지
    window.addEventListener('online', () => {
      console.log('🌐 네트워크 연결됨 - 큐된 메시지 처리 시작');
      this.isOnline = true;
      this.processQueue();
    });
    
    window.addEventListener('offline', () => {
      console.log('📴 네트워크 연결 끊김 - 메시지 큐잉 모드');
      this.isOnline = false;
    });
    
    // 페이지 로드 시 큐 복원
    this.loadQueueFromStorage();
  }

  // 메시지 큐에 추가
  async addMessage(messageData) {
    if (this.queue.length >= this.maxQueueSize) {
      console.warn('⚠️ 큐가 가득참 - 가장 오래된 메시지 제거');
      this.queue.shift();
    }

    const message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      data: messageData,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending'
    };

    this.queue.push(message);
    this.saveQueueToStorage();
    
    console.log(`📝 메시지 큐에 추가됨: ${message.id}`, messageData);

    // 온라인 상태면 즉시 처리
    if (this.isOnline) {
      this.processQueue();
    }
  }

  // 큐 처리
  async processQueue() {
    if (!this.isOnline || this.queue.length === 0) {
      return;
    }

    console.log(`🔄 큐 처리 시작 - ${this.queue.length}개 메시지`);

    const messagesToProcess = [...this.queue];
    this.queue = [];

    for (const message of messagesToProcess) {
      try {
        await this.sendMessage(message);
        console.log(`✅ 메시지 전송 성공: ${message.id}`);
      } catch (error) {
        console.error(`❌ 메시지 전송 실패: ${message.id}`, error);
        
        // 재시도 가능한 경우 큐에 다시 추가
        if (message.retryCount < this.maxRetries) {
          message.retryCount++;
          message.status = 'retrying';
          this.queue.push(message);
          console.log(`🔄 메시지 재시도 예약: ${message.id} (${message.retryCount}/${this.maxRetries})`);
        } else {
          console.error(`💀 메시지 최종 실패: ${message.id}`);
          message.status = 'failed';
        }
      }
    }

    this.saveQueueToStorage();
  }

  // 실제 메시지 전송
  async sendMessage(message) {
    const { data } = message;
    
    // API 엔드포인트에 따라 다른 처리
    if (data.type === 'notification') {
      return await this.sendNotificationMessage(data);
    } else if (data.type === 'fcm') {
      return await this.sendFcmMessage(data);
    } else {
      throw new Error(`알 수 없는 메시지 타입: ${data.type}`);
    }
  }

  // 알림 메시지 전송
  async sendNotificationMessage(data) {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/notifications/send-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      mode: 'cors', // 🔥 CORS 모드 명시
      credentials: 'include' // 🔥 인증 정보 포함
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  // FCM 메시지 전송
  async sendFcmMessage(data) {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/notifications/send-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      mode: 'cors', // 🔥 CORS 모드 명시
      credentials: 'include' // 🔥 인증 정보 포함
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  // 큐를 로컬 스토리지에 저장
  saveQueueToStorage() {
    try {
      localStorage.setItem('kairos_message_queue', JSON.stringify(this.queue));
    } catch (error) {
      console.error('❌ 큐 저장 실패:', error);
    }
  }

  // 로컬 스토리지에서 큐 복원
  loadQueueFromStorage() {
    try {
      const stored = localStorage.getItem('kairos_message_queue');
      if (stored) {
        this.queue = JSON.parse(stored);
        console.log(`📦 저장된 큐 복원: ${this.queue.length}개 메시지`);
      }
    } catch (error) {
      console.error('❌ 큐 복원 실패:', error);
      this.queue = [];
    }
  }

  // 큐 상태 조회
  getQueueStatus() {
    return {
      total: this.queue.length,
      pending: this.queue.filter(m => m.status === 'pending').length,
      retrying: this.queue.filter(m => m.status === 'retrying').length,
      failed: this.queue.filter(m => m.status === 'failed').length,
      isOnline: this.isOnline
    };
  }

  // 큐 초기화
  clearQueue() {
    this.queue = [];
    this.saveQueueToStorage();
    console.log('🗑️ 메시지 큐 초기화됨');
  }
}

// 싱글톤 인스턴스 생성
export const offlineMessageQueue = new OfflineMessageQueue();

